import { notifier } from "../../../core/notifier/notifier";
import { phpMaxRecommendRank } from "../../../core/php-data-access";
import { translator } from "../../../translation/translator";
import { z } from "zod";
import {
    GetBrandRecommendMeta200Response,
    GetBrandRecommendMeta401Response,
    GetBrandRecommendMeta403Response,
    GetBrandRecommendMeta404Response,
    GetBrandRecommendMeta500Response,
    RemoveBrandRecommend200Response,
    RemoveBrandRecommend400Response,
    RemoveBrandRecommend401Response,
    RemoveBrandRecommend403Response,
    RemoveBrandRecommend404Response,
    RemoveBrandRecommend500Response,
    UpsertBrandRecommend200Response,
    UpsertBrandRecommend400Response,
    UpsertBrandRecommend401Response,
    UpsertBrandRecommend403Response,
    UpsertBrandRecommend404Response,
    UpsertBrandRecommend409Response,
    UpsertBrandRecommend422Response,
    UpsertBrandRecommend500Response,
} from "../../../api/generated/brands.zod";
import { toPositiveIntOrNull } from "../../../util/id-utils";
import { brandRecommendForm } from "./brand-recommend-form";

type RecommendMetaResponse =
    | z.infer<typeof GetBrandRecommendMeta200Response>
    | z.infer<typeof GetBrandRecommendMeta401Response>
    | z.infer<typeof GetBrandRecommendMeta403Response>
    | z.infer<typeof GetBrandRecommendMeta404Response>
    | z.infer<typeof GetBrandRecommendMeta500Response>;

type RecommendMutationResponse =
    | z.infer<typeof UpsertBrandRecommend200Response>
    | z.infer<typeof UpsertBrandRecommend400Response>
    | z.infer<typeof UpsertBrandRecommend401Response>
    | z.infer<typeof UpsertBrandRecommend403Response>
    | z.infer<typeof UpsertBrandRecommend404Response>
    | z.infer<typeof UpsertBrandRecommend409Response>
    | z.infer<typeof UpsertBrandRecommend422Response>
    | z.infer<typeof UpsertBrandRecommend500Response>
    | z.infer<typeof RemoveBrandRecommend200Response>
    | z.infer<typeof RemoveBrandRecommend400Response>
    | z.infer<typeof RemoveBrandRecommend401Response>
    | z.infer<typeof RemoveBrandRecommend403Response>
    | z.infer<typeof RemoveBrandRecommend404Response>
    | z.infer<typeof RemoveBrandRecommend500Response>;

type ClearAllResponse = { success?: boolean; message?: string };

type RecommendButtonVm = { rank: number; disabled: boolean; isCurrent: boolean };

type BrandRecommendApiLike = {
    getMeta(brandId: number): Promise<Response>;
    upsert(brandId: number, rank: number | null): Promise<Response>;
    remove(brandId: number): Promise<Response>;
    clearAll(): Promise<Response>;
};

type BrandRecommendRenderLike = {
    setActions(actions: {
        onPickRank?: ((rank: number) => Promise<void> | void) | null;
        onUnset?: (() => Promise<void> | void) | null;
        onClearAll?: (() => Promise<void> | void) | null;
    }): void;
    renderBody(vm: { buttons: RecommendButtonVm[]; canUnset: boolean }): void;
    renderLoading(): void;
    open(): void;
    close(): void;
    setBusy(busy: boolean): void;
    setCrownState(el: HTMLElement, recommended: boolean): void;
    setCrownRank(el: HTMLElement, rank: number | null): void;
    getOverlay(): HTMLElement | null;
};

export class BrandRecommendHandler {
    public api: BrandRecommendApiLike;
    public render: BrandRecommendRenderLike;
    public activeBrandId: number | null;
    public activeCrownEl: HTMLElement | null;
    public busy: boolean;

    public constructor(deps: { api: BrandRecommendApiLike; render: BrandRecommendRenderLike }) {
        this.api = deps.api;
        this.render = deps.render;
        this.activeBrandId = null;
        this.activeCrownEl = null;
        this.busy = false;
    }

    public t(key: string, fallback: string): string {
        const value = translator?.getTranslation(key);
        return value && value !== key ? value : fallback;
    }

    public isSuccessResponse(value: unknown): value is 1 {
        return value === 1;
    }

    public getResponseMessage(value: unknown): string {
        if (!value || typeof value !== "object") return "";
        const message = "message" in value ? value.message : undefined;
        return typeof message === "string" ? message : "";
    }

    public isMetaResponse(value: unknown): value is RecommendMetaResponse {
        return !!value && typeof value === "object" && "success" in value;
    }

    public isMetaSuccessResponse(value: RecommendMetaResponse): value is z.infer<typeof GetBrandRecommendMeta200Response> {
        return this.isSuccessResponse(value.success) && "data" in value && !!value.data;
    }

    public isMutationResponse(value: unknown): value is RecommendMutationResponse {
        return !!value && typeof value === "object" && "success" in value;
    }

    public isMutationSuccessResponse(
        value: RecommendMutationResponse,
    ): value is z.infer<typeof UpsertBrandRecommend200Response> | z.infer<typeof RemoveBrandRecommend200Response> {
        return this.isSuccessResponse(value.success);
    }

    public async handleRecommendClick(event: MouseEvent, crownEl: HTMLElement): Promise<void> {
        event.preventDefault();
        event.stopPropagation();

        const brandId = toPositiveIntOrNull(crownEl.dataset.brandId);
        if (brandId === null) return;

        const card = brandRecommendForm.getBrandCard(crownEl);
        const canRecommend =
            crownEl.dataset?.canRecommend === "1" ||
            card?.dataset?.canRecommend === "1" ||
            !!brandRecommendForm.getRecommendCapabilityHost(crownEl);
        if (!canRecommend) return;

        if (this.busy || crownEl.dataset.loading === "1") return;

        this.activeBrandId = brandId;
        this.activeCrownEl = crownEl;

        this.render.setActions({
            onPickRank: async (rank) => await this.setRank(rank),
            onUnset: async () => await this.unsetRecommend(),
            onClearAll: async () => await this.clearAllRecommends(),
        });

        this.render.renderLoading();
        this.render.open();

        try {
            const meta = await this.fetchMeta(brandId);
            if (!meta) {
                this.render.close();
                return;
            }
            this.render.renderBody(meta);
        } catch (e) {
            console.error(e);
            notifier.error(this.t("brand_recommend_error_load", "Failed to load recommend info"));
            this.render.close();
        }
    }

    public async reloadMeta(): Promise<void> {
        if (!this.activeBrandId) return;
        this.render.renderLoading();
        try {
            const meta = await this.fetchMeta(this.activeBrandId);
            if (!meta) {
                this.render.close();
                return;
            }
            this.render.renderBody(meta);
        } catch (e) {
            console.error(e);
            notifier.error(this.t("brand_recommend_error_load", "Failed to load recommend info"));
            this.render.close();
        }
    }

    public async setRank(rank: number): Promise<void> {
        if (!this.activeBrandId || !this.activeCrownEl) return;
        this.render.renderLoading();
        this.busy = true;
        this.render.setBusy(true);
        try {
            const res = await this.api.upsert(this.activeBrandId, rank);
            const json = (await res.json().catch(() => ({}))) as unknown;
            if (!res.ok || !this.isMutationResponse(json) || !this.isMutationSuccessResponse(json)) {
                notifier.error(
                    this.getResponseMessage(json) || this.t("brand_recommend_error_update", "Failed to update recommend rank"),
                );
                await this.reloadMeta();
                return;
            }
            brandRecommendForm.getRecommendButtons(document, this.activeBrandId).forEach((el) => {
                this.render.setCrownState(el, true);
                this.render.setCrownRank(el, rank);
            });
            this.render.close();
        } catch (e) {
            console.error(e);
            notifier.error(this.t("brand_recommend_error_update", "Failed to update recommend rank"));
            await this.reloadMeta();
        } finally {
            this.render.setBusy(false);
            this.busy = false;
        }
    }

    public async unsetRecommend(): Promise<void> {
        if (!this.activeBrandId || !this.activeCrownEl) return;
        this.render.renderLoading();
        this.busy = true;
        this.render.setBusy(true);
        try {
            const res = await this.api.remove(this.activeBrandId);
            const json = (await res.json().catch(() => ({}))) as unknown;
            if (!res.ok || !this.isMutationResponse(json) || !this.isMutationSuccessResponse(json)) {
                notifier.error(
                    this.getResponseMessage(json) || this.t("brand_recommend_error_unset", "Failed to unset recommend"),
                );
                await this.reloadMeta();
                return;
            }
            brandRecommendForm.getRecommendButtons(document, this.activeBrandId).forEach((el) => {
                this.render.setCrownState(el, false);
                this.render.setCrownRank(el, null);
            });
            this.render.close();
        } catch (e) {
            console.error(e);
            notifier.error(this.t("brand_recommend_error_unset", "Failed to unset recommend"));
            await this.reloadMeta();
        } finally {
            this.render.setBusy(false);
            this.busy = false;
        }
    }

    public async clearAllRecommends(): Promise<void> {
        if (!this.activeBrandId) return;
        const ok = await notifier.confirm(this.t("brand_recommend_remove_all_confirm", "Remove all recommended ranks?"), {
            okTranslateKey: "remove_all",
            okText: this.t("remove_all", "Remove All"),
            cancelText: this.t("cancel", "Cancel"),
        });
        if (!ok) return;

        this.render.renderLoading();
        this.busy = true;
        this.render.setBusy(true);
        try {
            const res = await this.api.clearAll();
            const json = (await res.json().catch(() => ({}))) as ClearAllResponse;
            if (!res.ok || !json?.success) {
                notifier.error(json?.message || this.t("brand_recommend_error_remove", "Failed to clear recommends"));
                await this.reloadMeta();
                return;
            }
            brandRecommendForm.getRecommendButtons(document).forEach((el) => {
                this.render.setCrownState(el, false);
                this.render.setCrownRank(el, null);
            });
            this.render.close();
        } catch (e) {
            console.error(e);
            notifier.error(this.t("brand_recommend_error_remove", "Failed to clear recommends"));
            await this.reloadMeta();
        } finally {
            this.render.setBusy(false);
            this.busy = false;
        }
    }

    private async fetchMeta(brandId: number): Promise<{ buttons: RecommendButtonVm[]; canUnset: boolean } | null> {
        const res = await this.api.getMeta(brandId);
        const json = (await res.json().catch(() => ({}))) as unknown;

        if (!res.ok || !this.isMetaResponse(json) || !this.isMetaSuccessResponse(json)) {
            notifier.error(
                this.getResponseMessage(json) || this.t("brand_recommend_error_load", "Failed to load recommend info"),
            );
            return null;
        }

        const meta = json.data;
        const maxRank = Number(meta.max_rank || phpMaxRecommendRank());
        const usedRanks = Array.isArray(meta.used_ranks) ? meta.used_ranks : [];
        const currentRank = meta.current_rank === null || typeof meta.current_rank === "undefined" ? null : Number(meta.current_rank);
        const used = new Set(usedRanks.map((n) => Number(n)).filter((n) => Number.isFinite(n)));

        const buttons: RecommendButtonVm[] = [];
        for (let r = 1; r <= maxRank; r++) {
            const isUsed = used.has(r);
            const isCurrent = currentRank !== null && r === currentRank;
            buttons.push({ rank: r, disabled: isUsed && !isCurrent, isCurrent });
        }

        return { buttons, canUnset: currentRank !== null };
    }
}
